import { useEffect, useRef, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { resizeImage } from '@/lib/image';

interface BillPhotosProps {
  photos: Blob[];
  onAdd: (blob: Blob) => void;
  onRemove: (index: number) => void;
  max?: number;
  maxDimension?: number;
}

/**
 * Multi-photo capture grid. Lets the driver take up to {max} photos, shows a
 * thumbnail of every captured photo (so media "comes back" after capture) and
 * keeps a camera button available until the limit is reached -- mirroring the
 * native "Photos X/5" behavior.
 */
export function BillPhotos({ photos, onAdd, onRemove, max = 5, maxDimension = 1600 }: BillPhotosProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [urls, setUrls] = useState<string[]>([]);

  // Build/revoke object URLs whenever the photo set changes.
  useEffect(() => {
    const next = photos.map((b) => URL.createObjectURL(b));
    setUrls(next);
    return () => next.forEach((u) => URL.revokeObjectURL(u));
  }, [photos]);

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    setBusy(true);
    try {
      for (const file of files) {
        if (photos.length + 1 > max) break;
        const compressed = await resizeImage(file, maxDimension);
        onAdd(compressed);
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const atLimit = photos.length >= max;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-brand-muted">Photos</span>
        <span className="text-xs text-brand-muted">
          {photos.length}/{max}
        </span>
      </div>

      {urls.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {urls.map((u, i) => (
            <div key={u} className="relative">
              <img src={u} alt={`photo ${i + 1}`} className="h-24 w-full rounded-card object-cover" />
              <button
                type="button"
                aria-label={`Remove photo ${i + 1}`}
                onClick={() => onRemove(i)}
                className="absolute right-1 top-1 rounded-full bg-brand-error p-1.5 text-white shadow"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onChange}
        className="hidden"
      />
      <Button
        variant="outline"
        fullWidth
        size="lg"
        disabled={busy || atLimit}
        onClick={() => inputRef.current?.click()}
      >
        <Camera className="mr-2 h-5 w-5" />
        {busy ? 'Processing...' : atLimit ? `Max ${max} photos` : `Add photo (${photos.length}/${max})`}
      </Button>
    </div>
  );
}
